import React, {useState} from 'react';
import {useAsync} from "react-async-hook";
import { useLocation } from "react-router-dom";
import {useForm} from "react-hook-form";
import {useSystemMessage} from "../hooks/useSystemMessage";
import {userApi} from "../apis/user/userApi";
import useLoading from "../hooks/useLoading";
import useAppRouter from "../hooks/useAppRouter";
import userIcon from "../assets/images/user_icon.png";
import companyIcon from "../assets/images/company_icon.png";
import passwordIcon from "../assets/images/password_icon.png";
import {PasswordDisplayButton} from "../components/pages/common/PasswordDisplayButton";
import {FormValidationType, makeRegisterOptions} from "../libs/reactHookForm/makeRegisterOptions";
import {ValidationErrorMessages} from "../components/pages/common/ValidationErrorMessages";
import {ApiClientError} from "../apis/errors";
import {logError} from "../libs/utils/logError";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export interface CustomerConfirmFormValues {
  name: string;
  companyName: string;
  zip: string;
  address: string;
  building: string;
  tel: string;
  password: string;
  confirmPassword: string;
  department: string;
  position: string;
}

export const CustomerConfirmPage: React.FC = () => {
  const {success} = useSystemMessage();
  const [abortController, setAbortController] = useState<AbortController | undefined>();
  const [ isShowPassword, setIsShowPassword ] = useState<boolean>(false);
  const [ isShowConfirmPassword, setIsShowConfirmPassword ] = useState<boolean>(false);
  const [ apiClientError, setApiClientError ] = useState<ApiClientError | null>(null);
  const withLoading = useLoading();
  const location = useLocation();
  const { goto } = useAppRouter();
  const query = useQuery();
  const token = query.get('token');
  const {
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    reValidateMode: 'onSubmit',
  });
  const password = watch('password');

  useAsync(async () => {
    const fetch = async () => {
      if (token === null) {
        goto('notFoundError');
        return;
      }

      setAbortController(new AbortController());
      try
      {
        const response = await userApi.showTempCustomer(token, { abortSignal: abortController?.signal });
        setAbortController(undefined);
        setValue('name', response.user.name);
        setValue('companyName', response.user.companyName);
        setValue('zip', response.user.extraCustomer.zip);
        setValue('address', response.user.extraCustomer.address);
        setValue('building', response.user.extraCustomer.building);
        setValue('tel', response.user.extraCustomer.tel);
        setValue('department', response.user.extraCustomer.department);
        setValue('position', response.user.extraCustomer.position);

      } catch (e) {
        await logError(
          location.pathname,
          '仮顧客情報の取得に失敗しました。',
          e
        );

        if (e instanceof ApiClientError) {
          setApiClientError(e);
          return;
        }

        goto('internalServerError');
      }
    };
    withLoading(fetch);
  }, []);

  const onSubmit = async (data: CustomerConfirmFormValues) => {
    const fetch = async () => {
      setAbortController(new AbortController());
      try {
        await userApi.updateTempCustomer({
          token: token ?? '',
          name: data.name,
          password: data.password,
          companyName: data.companyName,
          extraCustomer: {
            zip: data.zip,
            address: data.address,
            building: data.building,
            tel: data.tel,
            department: data.department,
            position: data.position,
          }
        }, {abortSignal: abortController?.signal});
        setAbortController(undefined);

        await success('アカウントの確認が完了しました');
        goto('login');

      } catch (e) {
        await logError(
          location.pathname,
          'アカウントの確認に失敗しました。',
          e
        );

        if (e instanceof ApiClientError) {
          setApiClientError(e);
          return;
        }

        goto('internalServerError');
      }
    };
    withLoading(fetch);
  };

  return (
    <>
      <form className="bg-primary flex flex-col justify-between min-w-full min-h-full py-12 px-16">
        <div>
          <div className="text-2xl text-white font-bold">アカウント登録内容確認</div>
          <hr className="w-8 border-4 border-white mt-2 mb-6" />
          <div className="mb-1 text-danger-secondary text-xs">
            { apiClientError?.message }
            { (Object.keys(errors).length > 0) && '入力エラーがあります' }
          </div>
        </div>

        <div>
          { /* お名前 */ }
          <div className="mb-2">
            <label htmlFor="name">
              <div className="flex items-center">
                  <span className="w-3 mr-1">
                    <img src={userIcon} className="object-contain" alt="icon" />
                  </span>
                <input
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...register("name",
                    makeRegisterOptions('お名前', [
                      { type: FormValidationType.REQUIRED },
                      { type: FormValidationType.MAX_STRING_LENGTH, value: 254 },
                    ])
                  )}
                  id="email"
                  className="flex-grow bg-transparent text-white text-sm border-b-2 border-white placeholder-white placeholder-opacity-40 focus:outline-none p-2"
                  type="text"
                  maxLength={254}
                  autoComplete="name"
                  placeholder="お名前"
                />
              </div>
              <div className="ml-6">
                <div className="mt-1 text-danger-secondary text-xs">
                  {errors.name?.message}
                </div>
                <ValidationErrorMessages
                  {...{
                    fieldErrors: apiClientError?.errors,
                    field: 'name',
                    textColorClass: 'text-danger-secondary',
                  }}
                />
              </div>
            </label>
          </div>

          { /* 会社名 */ }
          <div className="mb-2">
            <label htmlFor="companyName">
              <div className="flex items-center">
                  <span className="w-3 mr-1">
                    <img src={companyIcon} className="object-contain" alt="icon" />
                  </span>
                <input
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...register("companyName",
                    makeRegisterOptions('会社名', [
                      { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                    ])
                  )}
                  id="companyName"
                  className="flex-grow bg-transparent text-white text-sm border-b-2 border-white placeholder-white placeholder-opacity-40 focus:outline-none p-2"
                  type="text"
                  maxLength={100}
                  autoComplete="companyName"
                  placeholder="会社名"
                />
              </div>
              <div className="ml-6">
                <div className="mt-1 text-danger-secondary text-xs">
                  {errors.companyName?.message}
                </div>
                <ValidationErrorMessages
                  {...{
                    fieldErrors: apiClientError?.errors,
                    field: 'companyName',
                    textColorClass: 'text-danger-secondary',
                  }}
                />
              </div>
            </label>
          </div>

          { /* 部署 */ }
          <div className="mb-2">
            <label htmlFor="department">
              <div className="flex items-center">
                <input
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...register("department",
                    makeRegisterOptions('部署', [
                      { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                    ])
                  )}
                  id="department"
                  className="flex-grow bg-transparent text-white text-sm border-b-2 border-white placeholder-white placeholder-opacity-40 focus:outline-none p-2 ml-4"
                  type="text"
                  maxLength={100}
                  autoComplete="department"
                  placeholder="部署"
                />
              </div>
              <div className="ml-6">
                <div className="mt-1 text-danger-secondary text-xs">
                  {errors.department?.message}
                </div>
                <ValidationErrorMessages
                  {...{
                    fieldErrors: apiClientError?.errors,
                    field: 'department',
                    textColorClass: 'text-danger-secondary',
                  }}
                />
              </div>
            </label>
          </div>

          { /* 役職 */ }
          <div className="mb-2">
            <label htmlFor="position">
              <div className="flex items-center">
                <input
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...register("position",
                    makeRegisterOptions('役職', [
                      { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                    ])
                  )}
                  id="position"
                  className="flex-grow bg-transparent text-white text-sm border-b-2 border-white placeholder-white placeholder-opacity-40 focus:outline-none p-2 ml-4"
                  type="text"
                  maxLength={100}
                  autoComplete="position"
                  placeholder="役職"
                />
              </div>
              <div className="ml-6">
                <div className="mt-1 text-danger-secondary text-xs">
                  {errors.position?.message}
                </div>
                <ValidationErrorMessages
                  {...{
                    fieldErrors: apiClientError?.errors,
                    field: 'position',
                    textColorClass: 'text-danger-secondary',
                  }}
                />
              </div>
            </label>
          </div>

          { /* 郵便番号 */ }
          <div className="mb-2">
            <label htmlFor="zip">
              <div className="flex items-center">
                <input
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...register("zip",
                    makeRegisterOptions('郵便番号', [
                      { type: FormValidationType.MAX_STRING_LENGTH, value: 7 },
                    ])
                  )}
                  id="zip"
                  className="flex-grow bg-transparent text-white text-sm border-b-2 border-white placeholder-white placeholder-opacity-40 focus:outline-none ml-4 p-2"
                  type="text"
                  maxLength={7}
                  autoComplete="zip"
                  placeholder="郵便番号"
                />
              </div>
              <div className="ml-6">
                <div className="mt-1 text-danger-secondary text-xs">
                  {errors.zip?.message}
                </div>
                <ValidationErrorMessages
                  {...{
                    fieldErrors: apiClientError?.errors,
                    field: 'zip',
                    textColorClass: 'text-danger-secondary',
                  }}
                />
              </div>
            </label>
          </div>

          { /* 住所 */ }
          <div className="mb-2">
            <label htmlFor="address">
              <div className="flex items-center">
                <input
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...register("address",
                    makeRegisterOptions('住所', [
                      { type: FormValidationType.MAX_STRING_LENGTH, value: 255 },
                    ])
                  )}
                  id="address"
                  className="flex-grow bg-transparent text-white text-sm border-b-2 border-white placeholder-white placeholder-opacity-40 focus:outline-none ml-4 p-2"
                  type="text"
                  maxLength={255}
                  autoComplete="address"
                  placeholder="住所"
                />
              </div>
              <div className="ml-6">
                <div className="mt-1 text-danger-secondary text-xs">
                  {errors.address?.message}
                </div>
                <ValidationErrorMessages
                  {...{
                    fieldErrors: apiClientError?.errors,
                    field: 'address',
                    textColorClass: 'text-danger-secondary',
                  }}
                />
              </div>
            </label>
          </div>

          { /* 建物名 */ }
          <div className="mb-2">
            <label htmlFor="building">
              <div className="flex items-center">
                <input
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...register("building",
                    makeRegisterOptions('建物名', [
                      { type: FormValidationType.MAX_STRING_LENGTH, value: 50 },
                    ])
                  )}
                  id="building"
                  className="flex-grow bg-transparent text-white text-sm border-b-2 border-white placeholder-white placeholder-opacity-40 focus:outline-none ml-4 p-2"
                  type="text"
                  maxLength={50}
                  autoComplete="building"
                  placeholder="建物名"
                />
              </div>
              <div className="ml-6">
                <div className="mt-1 text-danger-secondary text-xs">
                  {errors.building?.message}
                </div>
                <ValidationErrorMessages
                  {...{
                    fieldErrors: apiClientError?.errors,
                    field: 'building',
                    textColorClass: 'text-danger-secondary',
                  }}
                />
              </div>
            </label>
          </div>

          { /* 電話番号 */ }
          <div className="mb-2">
            <label htmlFor="tel">
              <div className="flex items-center">
                <input
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...register("tel",
                    makeRegisterOptions('電話番号', [
                      { type: FormValidationType.MAX_STRING_LENGTH, value: 20 },
                    ])
                  )}
                  id="tel"
                  className="flex-grow bg-transparent text-white text-sm border-b-2 border-white placeholder-white placeholder-opacity-40 focus:outline-none ml-4 p-2"
                  type="text"
                  maxLength={20}
                  autoComplete="tel"
                  placeholder="電話番号"
                />
              </div>
              <div className="ml-6">
                <div className="mt-1 text-danger-secondary text-xs">
                  {errors.tel?.message}
                </div>
                <ValidationErrorMessages
                  {...{
                    fieldErrors: apiClientError?.errors,
                    field: 'tel',
                    textColorClass: 'text-danger-secondary',
                  }}
                />
              </div>
            </label>
          </div>

          { /* パスワード */ }
          <div className="mb-2">
            <label htmlFor="password">
              <div className="flex items-center">
                  <span className="w-3 mr-1">
                    <img src={passwordIcon} className="object-contain" alt="icon" />
                  </span>
                <span className="relative flex-grow">
                    <input
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...register("password",
                        makeRegisterOptions('パスワード', [
                          { type: FormValidationType.REQUIRED },
                          { type: FormValidationType.MAX_STRING_LENGTH, value: 64 },
                          { type: FormValidationType.PASSWORD, typeName: 'password' },
                        ])
                      )}
                      id="password"
                      className="min-w-full bg-transparent text-white text-sm border-b-2 border-white placeholder-white placeholder-opacity-40 focus:outline-none p-2"
                      type={isShowPassword ? "text" : "password"}
                      maxLength={64}
                      autoComplete="current-password"
                      placeholder="パスワード"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 text-white flex items-center text-sm leading-5">
                      <PasswordDisplayButton
                        {...{
                          isShow: isShowPassword,
                          setIsShow: setIsShowPassword,
                        }}
                      />
                    </div>
                  </span>
              </div>
              <div className="ml-6">
                <div className="mt-1 text-danger-secondary text-xs">
                  {errors.password?.message}
                </div>
                <ValidationErrorMessages
                  {...{
                    fieldErrors: apiClientError?.errors,
                    field: 'password',
                    textColorClass: 'text-danger-secondary',
                  }}
                />
              </div>
            </label>
          </div>

          { /* パスワード（確認） */ }
          <div className="mb-2">
            <label htmlFor="confirmPassword">
              <div className="flex items-center">
                  <span className="w-3 mr-1">
                    <img src={passwordIcon} className="object-contain" alt="icon" />
                  </span>
                <span className="relative flex-grow">
                    <input
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...register("confirmPassword",
                        makeRegisterOptions('パスワード（確認）', [
                          { type: FormValidationType.REQUIRED },
                          { type: FormValidationType.MAX_STRING_LENGTH, value: 64 },
                          { type: FormValidationType.PASSWORD, typeName: 'password' },
                          {
                            type: FormValidationType.CONFIRMED,
                            otherFieldName: 'パスワード',
                            otherFieldValue: password,
                            typeName: 'confirmedPassword',
                          },
                        ])
                      )}
                      id="confirmPassword"
                      className="min-w-full bg-transparent text-white text-sm border-b-2 border-white placeholder-white placeholder-opacity-40 focus:outline-none p-2"
                      type={isShowConfirmPassword ? "text" : "password"}
                      maxLength={64}
                      placeholder="パスワード（確認）"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 text-white flex items-center text-sm leading-5">
                      <PasswordDisplayButton
                        {...{
                          isShow: isShowConfirmPassword,
                          setIsShow: setIsShowConfirmPassword,
                        }}
                      />
                    </div>
                  </span>
              </div>
              <div className="ml-6">
                <div className="mt-1 text-danger-secondary text-xs">
                  {errors.confirmPassword?.message}
                </div>
              </div>
            </label>
          </div>
        </div>

        <div className="flex justify-center">
          <button
            className="btn-primary text-6xl border-2 border-white rounded"
            type="button"
            onClick={async () => {
              setApiClientError(null);
              await handleSubmit(onSubmit)();
            }}
          >
            ENTER
          </button>
        </div>
      </form>
    </>
  );
};
